import React from 'react';
import CompanyInfo from '../_ui/CompanyInfo';
import TicketEstimate from './TicketEstimate';
import SelectorInput from '../_ui/formElements/SelectorInput';
import DatePicker from 'react-widgets/DatePicker';
import {convertDateToTimestamp, formatDate} from '../../_utilities/formattingUtils';
import {calculateBusinessDays} from '../../_utilities/reactUtils';
import {TICKET_STATUS} from '../../_utilities/ticketUpdateService';

export default function TicketSidebar({ticket, groupId, ticketCost, agentList, types, priorities, statuses,
    status, assignee, saving, onSave, onEstimateEditingChange, onControlEditingChange, onError}) {
    // The API timestamp produced by convertDateToTimestamp is UTC without a timezone suffix.
    const dueValue = ticket.date_due && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ticket.date_due)
        ? ticket.date_due.replace(' ', 'T') + 'Z' : ticket.date_due;
    const parsedDate = dueValue ? new Date(dueValue) : null;
    const dateDue = parsedDate && Number.isFinite(parsedDate.getTime()) ? parsedDate : null;
    const handleDateChange = value => {
        if (value === null) return onSave('date_due', null);
        const date = value instanceof Date ? new Date(value.getTime()) : calculateBusinessDays(new Date(), 3);
        if (!Number.isFinite(date.getTime())) {
            onError('Enter a valid due date.');
            return;
        }
        date.setHours(16, 0, 0, 0);
        return onSave('date_due', convertDateToTimestamp(date));
    };
    return (
            <div id='ticket-aside' className="py-2 px-3 bg-secondary bg-opacity-10">
                {ticket ? <CompanyInfo company_id={ticket.company_id} contact_id={ticket.contact_id}/> : null}

                <TicketEstimate ticket={ticket} groupId={groupId} ticketCost={ticketCost}
                    saving={saving} onSave={onSave} onEditingChange={onEstimateEditingChange} onError={onError}/>
                {ticket && ticket.date_due ?
                    <div className={`text-center mb-3 h3`}>Due Date: {formatDate(ticket.date_due)}</div> : null}

                <div className={`my-3`}>
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.BACKLOG ?
                        <button disabled={saving} className={`btn btn-success w-100 mb-1`}
                                onClick={() => onSave('status_id', TICKET_STATUS.ASSIGNED)}>Start Ticket
                        </button> : null}
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.NEEDS_INFO ?
                        <button disabled={saving} className={`btn btn-success w-100 mb-1`}
                                onClick={() => window.confirm("Please note: this will move the ticket back to the Assigned list to be worked on. Please remember to add comments regarding needed information.") ? onSave('status_id', TICKET_STATUS.ASSIGNED) : null}>Info Provided</button> : null}
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.REVIEW ?
                        <button disabled={saving} className={`btn btn-warning w-100 mb-1`}
                                onClick={() => window.confirm("Please note: this will move the ticket back to the Assigned list to be worked on. Please remember to add comments regarding additional work.") ? onSave('status_id', TICKET_STATUS.ASSIGNED) : null}>Needs
                            More Work</button> : null}
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.REVIEW ?
                        <button disabled={saving} className={`btn btn-warning w-100 mb-1`}
                                onClick={() => window.confirm("Please note: this will move the ticket back to the Assigned list to be launched.") ? onSave('status_id', TICKET_STATUS.READY_TO_LAUNCH) : null}>Ready To Launch</button> : null}
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.REVIEW ?
                        <button disabled={saving} className={`btn btn-success w-100 mb-1`}
                                onClick={() => window.confirm("Please note: this will remove the ticket from your list. Continue?") ? onSave('status_id', TICKET_STATUS.COMPLETED) : null}>Mark
                            Complete</button> : null}
                    {ticket && ticket.status_id && Number(ticket.status_id) <= Number(TICKET_STATUS.REVIEW) ?
                        <button disabled={saving} className={`btn btn-danger w-100`}
                                onClick={() => window.confirm("Please note: you will still be charged for time spent on this ticket. Continue?") ? onSave('status_id', TICKET_STATUS.CANCELLED) : null}>Cancel
                            Ticket
                        </button> : null}
                    {ticket && ticket.status_id && ticket.status_id === TICKET_STATUS.COMPLETED ?
                        <button disabled={saving} className={`btn btn-warning w-100`}
                                onClick={() => onSave('status_id', TICKET_STATUS.BACKLOG)}>Reopen Ticket
                        </button> : null}

                </div>

                Type:
                <SelectorInput disabled={saving} inputClass={`mb-3`}
                               inputName={`type_id`}
                               inputValue={ticket && ticket.type_id ? ticket.type_id : ''}
                               inputOptions={types?.defaults?.map ? types.defaults.map(item => ({ label: item.name, value: Number(item.id) })): []}
                               onOpen={opened => onControlEditingChange('selector', opened)}
                               onChange={(value) => onSave('type_id', value)}
                />

                Priority:
                <SelectorInput disabled={saving} inputClass={`mb-3`}
                               inputName={`priority_id`}
                               inputValue={ticket && ticket.priority_id ? ticket.priority_id : ''}
                               inputOptions={priorities?.defaults?.map ? priorities.defaults.map(item => ({ label: item.name, value: Number(item.id) })): []}
                               onOpen={opened => onControlEditingChange('selector', opened)}
                               onChange={(value) => onSave('priority_id', value)}
                />

                Due Date:
                <DatePicker disabled={saving} id='ticket-due-date' className='datepicker mb-3'
                            value={dateDue}
                            dropUp={true}
                            placeholder={'mm/dd/yyyy'}
                            onChange={handleDateChange}
                             onFocus={() => onControlEditingChange('dueDate', true)}
                             onBlur={() => onControlEditingChange('dueDate', false)}
                />

                <div className={`d-flex justify-content-between`}>
                    <span>Status:</span>
                    <label className="d-flex align-items-center">
                        <input type="checkbox" className="me-2" disabled={saving}
                            checked={Number(ticket.on_hold) > 0}
                            onChange={event => onSave('on_hold', event.target.checked ? 1 : 0)}/>
                        On Hold
                    </label>
                </div>
                {Number(groupId) > 0 && Number(groupId) <= 4 && agentList.length > 0 ?
                    <SelectorInput disabled={saving} inputClass={`mb-3`}
                                   inputName={`status_id`}
                                   inputValue={ticket && ticket.status_id ? ticket.status_id : ''}
                                   inputOptions={statuses?.defaults?.map ? statuses.defaults.map(item => ({ label: item.name, value: Number(item.id) })): []}
                                   onOpen={opened => onControlEditingChange('selector', opened)}
                                   onChange={(value) => onSave('status_id', value)}
                    /> : <input type='text' disabled
                                value={status?.name || ''}
                                className='mb-3 form-control'/>}


                Assigned Agent:
                {Number(groupId) > 0 && Number(groupId) <= 4 && agentList.length > 0 ?
                    <SelectorInput disabled={saving} inputClass={`mb-3`}
                                   inputInstructions={`Assign to...`}
                                   inputName={`assignee_id`}
                                   inputValue={ticket.assignee_id || 'null'}
                                   inputOptions={agentList && agentList.map ? agentList.map(item => {
                                       return {label: `${item.first_name} ${item.last_name}`, value: item.contact_id}
                                   }) : null}
                                   onOpen={opened => onControlEditingChange('selector', opened)}
                                   onChange={(value) => onSave('assignee_id', value)}
                    /> : <input type='text' disabled
                                value={assignee ? `${assignee.first_name} ${assignee.last_name}` : 'Unassigned'}
                                className='mb-3 form-control'/>}

                {ticketCost && (groupId === "5" || groupId === "1") ? <>
                    Current Ticket Value:<br/>
                    <span style={{fontSize: '2rem'}}>{ticketCost}</span>
                </> : null}
            </div>
    );
}
